import { rankItem } from '@tanstack/match-sorter-utils';
import type { RankingInfo } from '@tanstack/match-sorter-utils';
import type { Column, FilterFn, Table, TableFeatures } from '@tanstack/react-table';
import type { HTMLProps } from 'react';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import HubertInput from 'components/HubertUI/HubertInput';

export interface FuzzyFilterMeta {
  itemRank?: RankingInfo;
}

export function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate;
    }
  }, [ref, indeterminate]);

  return <input type="checkbox" ref={ref} className={className + ' cursor-pointer'} {...rest} />;
}

export function Filter({
  column,
  table,
}: {
  column: Column<TableFeatures & { filterMeta: FuzzyFilterMeta }, any>;
  table: Table<TableFeatures & { filterMeta: FuzzyFilterMeta }, any>;
}) {
  const { t } = useTranslation('PaginatedManagerTable');
  const firstValue = table.getPreFilteredRowModel().flatRows[0]?.getValue(column.id);

  return typeof firstValue === 'number' ? (
    <div className="flex space-x-2">
      <HubertInput
        type="number"
        value={((column.getFilterValue() as any)?.[0] ?? '') as string}
        onChange={(e) => column.setFilterValue((old: any) => [e.target.value, old?.[1]])}
        placeholder={t('helpers.min')}
        className="w-24 border"
      />
      <HubertInput
        type="number"
        value={((column.getFilterValue() as any)?.[1] ?? '') as string}
        onChange={(e) => column.setFilterValue((old: any) => [old?.[0], e.target.value])}
        placeholder={t('helpers.max')}
        className="w-24 rounded border shadow"
      />
    </div>
  ) : (
    <HubertInput
      type="text"
      value={(column.getFilterValue() ?? '') as string}
      onChange={(e) => column.setFilterValue(e.target.value)}
      placeholder={t('helpers.searchPlaceholder')}
    />
  );
}

export const fuzzyFilter: FilterFn<TableFeatures & { filterMeta: FuzzyFilterMeta }, any> = (
  row,
  columnId,
  value,
  addMeta,
) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta?.({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};
